import {
  createContext, FC, ReactNode, useCallback, useContext, useMemo,
  useState,
} from 'react';
import {
  DepartureTime, TripCartDuration, TripCartFormState,
  TripCartMultiDayPrice,
  TripCartSeasonData,
} from 'src/listing/types';
import { formatDate } from 'src/common/utils/dateHelpers';
import { useForm, useFormState } from 'react-final-form';
import useDateFnsLocale from 'src/common/hooks/useDateFnsLocale';
import { getDurationHours } from 'src/listing/helper';
import { PERIOD_DAY } from 'src/admin/pricing/constants';
import { FullBoatSearchDetail } from 'src/common/types/api/v4/boats/search';
import broadcastNotification from 'src/common/components/Notification/broadcastNotification';
import { addHours, isAfter, isBefore } from 'date-fns';
import { timeStringToDate } from 'src/common/helpers';
import { BlockedTime } from 'src/common/providers/useBookingMotivators';
import {
  formatToTimeOfDay,
  getDepartureTimesForDuration,
  getDepartureTimesFromOperatingHours,
  getDurationFromMultiDayPrices,
  getDurationFromPriceSets,
  getTripCartDepartureTimes,
} from '../helper';
import { TripCartDurationOption } from '../utils/tripCartUtils';
import getSeasonPriceSetsForDate from '../utils/getSeasonPriceSetsForDate';
import { getCachedSeasonForDate, setCachedSeasonForDate } from '../utils/tripCartCache';
import { useTripCartContext } from './useTripCartContext';

type TripCartSeasonsContextProps = {
  tripCartSeasonData: TripCartSeasonData | null;
  multiDaysDurationsForDate: TripCartDurationOption[];
  hasDayTierPrices: boolean;
  durationsForDate: TripCartDurationOption[];
  departureTimesForDate: DepartureTime[];
  setupSeasonForDate: (date: Date, blockedTimes?: BlockedTime[]) => Promise<boolean>;
  setupDepartureTimesForDuration: ({
    duration,
    initialTripCartSeasonData,
    blockedTimes,
  }: {
    duration: number;
    initialTripCartSeasonData?: TripCartSeasonData;
    blockedTimes?: BlockedTime[];
  }) => void;
  priceSets?: TripCartDuration[];
  setupDepartureTimesForMultiDayPricing: () => void;
};

const TripCartSeasonsContext = createContext<TripCartSeasonsContextProps | null>(null);

type TripCartSeasonsProps = {
  listing: FullBoatSearchDetail;
  children: ReactNode;
};

const TripCartSeasonsProvider: FC<
TripCartSeasonsProps
> = ({ children, listing }) => {
  const { change } = useForm<TripCartFormState>();
  const {
    values: {
      departureTime: formDepartureTime,
      formSelectedDates,
      isMultiDay,
      formDuration,
    },
  } = useFormState<TripCartFormState>();
  const { setHasDepartureTimes } = useTripCartContext();
  const locale = useDateFnsLocale();
  const [selectedDate] = formSelectedDates;
  const [tripCartSeasonData, setSeasonData] = useState<TripCartSeasonData | null>(null);
  const [
    multiDaysDurationsForDate,
    setMultiDaysDurationsForDate,
  ] = useState<TripCartDurationOption[]>([]);
  const [hasDayTierPrices, setHasDayTierPrices] = useState<boolean>(false);
  const [durationsForDate, setDurationsForDate] = useState<TripCartDurationOption[]>([]);
  const [departureTimesForDate, setDepartureTimesForDate] = useState<DepartureTime[]>([]);

  const setupDepartureTimesForDuration = useCallback(({
    duration,
    initialTripCartSeasonData,
    blockedTimes,
  }: {
    duration: number;
    initialTripCartSeasonData?: TripCartSeasonData;
    blockedTimes?: BlockedTime[];
  }) => {
    const priceSets = initialTripCartSeasonData?.durations ?? tripCartSeasonData?.durations ?? [];
    const operatingHoursDeparture = initialTripCartSeasonData?.operatingHoursDeparture
    ?? tripCartSeasonData?.operatingHoursDeparture ?? { hours: 0, minutes: 0 };
    const operatingHoursReturn = initialTripCartSeasonData?.operatingHoursReturn
    ?? tripCartSeasonData?.operatingHoursReturn ?? { hours: 0, minutes: 0 };

    // get departure times for a specific duration
    const departureTimes = getDepartureTimesForDuration(
      priceSets,
      duration,
    );

    const tripCartDepartureTimes: DepartureTime[] = departureTimes.length
      ? getTripCartDepartureTimes(departureTimes, locale)
      : getDepartureTimesFromOperatingHours({
        operatingHoursDeparture,
        operatingHoursReturn,
        selectedDate,
        locale,
        listingTimeZone: listing.timezone,
      });

    const durationHours = getDurationHours(formDuration);
    // remove departure times that are blocked
    const unblockedDepartureTimes = tripCartDepartureTimes.filter(
      (departureTime) => {
        const dateTimeDuration = addHours(departureTime.dateTime, durationHours);
        const blocked = blockedTimes?.some((range) => {
          const start = timeStringToDate(range.start_time, selectedDate);
          const end = timeStringToDate(range.end_time, selectedDate);
          return isAfter(dateTimeDuration, start) && isBefore(departureTime.dateTime, end);
        });
        return !blocked;
      },
    );

    setDepartureTimesForDate(unblockedDepartureTimes);
    setHasDepartureTimes(unblockedDepartureTimes.length > 0);
    if (!unblockedDepartureTimes.find(
      ({ formattedTime24 }) => formattedTime24 === formDepartureTime,
    )) {
      change('departureTime', undefined);
    }
  }, [
    change,
    formDepartureTime,
    locale,
    tripCartSeasonData,
    selectedDate,
    listing.timezone,
    formDuration,
    setHasDepartureTimes,
  ]);

  const setupDepartureTimesForMultiDayPricing = useCallback(() => {
    if (listing.calendar_seasons?.operating_hours_departure
      && listing.calendar_seasons?.operating_hours_return) {
      const departureTimes = getDepartureTimesFromOperatingHours({
        operatingHoursDeparture: formatToTimeOfDay(
          listing.calendar_seasons?.operating_hours_departure,
        ),
        operatingHoursReturn: formatToTimeOfDay(listing.calendar_seasons?.operating_hours_return),
        selectedDate,
        locale,
        listingTimeZone: listing.timezone,
      });
      setDepartureTimesForDate(departureTimes);
      setHasDepartureTimes(departureTimes.length > 0);
    }
  }, [
    listing.calendar_seasons?.operating_hours_departure,
    listing.calendar_seasons?.operating_hours_return,
    locale,
    selectedDate,
    listing.timezone,
    setHasDepartureTimes,
  ]);

  const setupDurationsForMultiDayPricing = useCallback((multiDays: TripCartMultiDayPrice[]) => {
    const hasDay = multiDays.some((multiDay) => multiDay.period === PERIOD_DAY);
    setHasDayTierPrices(hasDay);
    const tripCartDurations = getDurationFromMultiDayPrices(multiDays, hasDay ? 'day' : 'night');
    setMultiDaysDurationsForDate(tripCartDurations);
    if (isMultiDay) {
      setupDepartureTimesForMultiDayPricing();
    }
  }, [isMultiDay, setupDepartureTimesForMultiDayPricing]);

  const setupSeasonForDate = useCallback(async (
    date: Date,
    blockedTimes: BlockedTime[] = [],
  ): Promise<boolean> => {
    let seasonData: TripCartSeasonData | null = null;

    // Retrieve cached season for date if available
    const cache = getCachedSeasonForDate({ date: formatDate(date) });

    // If no cached data, retrieve from API and cache the result
    seasonData = !cache
      ? await getSeasonPriceSetsForDate(listing.id, formatDate(date))
      : cache;

    if (!seasonData) {
      broadcastNotification({
        i18nKey: 'trip-cart--form-error--general',
        translation: 'listing-page',
        variant: 'error',
      });
      return false;
    }

    setSeasonData(seasonData);
    // Setup durations for multi day pricing
    if (seasonData?.multiDayPrices.length) {
      setupDurationsForMultiDayPricing(seasonData.multiDayPrices);
    }

    // Setup durations for same day pricing
    if (seasonData?.durations?.length) {
      const tripCartDurations = getDurationFromPriceSets(seasonData.durations);
      setDurationsForDate(tripCartDurations);

      if (tripCartDurations.length > 0 && !isMultiDay) {
        const hasDuration = tripCartDurations.some(
          ({ durationFormatted }) => durationFormatted === formDuration,
        );
        const duration = hasDuration
          ? getDurationHours(formDuration)
          : tripCartDurations[0].duration;

        // Setup Initial departure times.
        setupDepartureTimesForDuration({
          duration,
          initialTripCartSeasonData: seasonData,
          blockedTimes,
        });

        // set initial duration
        change('formDuration', hasDuration ? formDuration : tripCartDurations[0].durationFormatted);

        // Cache the season data if it was not retrieved from cache
        if (!cache) {
          setCachedSeasonForDate({ date: formatDate(date) }, seasonData);
        }
      }
    }
    return true;
  }, [
    change,
    formDuration,
    listing,
    isMultiDay,
    setupDepartureTimesForDuration,
    setupDurationsForMultiDayPricing,
  ]);

  const value = useMemo(() => ({
    tripCartSeasonData,
    durationsForDate,
    multiDaysDurationsForDate,
    departureTimesForDate,
    hasDayTierPrices,
    setupSeasonForDate,
    setupDepartureTimesForDuration,
    setupDepartureTimesForMultiDayPricing,
  }), [
    setupSeasonForDate,
    durationsForDate,
    multiDaysDurationsForDate,
    departureTimesForDate,
    hasDayTierPrices,
    tripCartSeasonData,
    setupDepartureTimesForDuration,
    setupDepartureTimesForMultiDayPricing,
  ]);
  return (
    <TripCartSeasonsContext.Provider value={value}>
      {children}
    </TripCartSeasonsContext.Provider>
  );
};

export default TripCartSeasonsProvider;

export const useTripCartSeasonsContext = (): TripCartSeasonsContextProps => {
  const context = useContext(TripCartSeasonsContext);
  if (!context) {
    throw new Error('useTripCartSeasonsContext must be used within a TripCartSeasonsProvider');
  }
  return context;
};
